import { Button, Input, Spin } from "antd";
import { useFormik } from "formik";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { useGetMyUserDetailsFromAuthBESession } from "../api/api.get";
import { useUpdateProfile, useUpdateProfilePhone } from "../api/api.update";
import { useNotifications } from "../components/notifications/notification";
import { COUNTRY_CODE } from "../utils/constants";
import { capitalize, capitalizeFirst } from "../utils/functions";
import ConfirmDialog from "../components/dialog/confirm.dialog";
import { useState } from "react";
import { useDeleteMe } from "../api/api.delete";

export default function ProfilePage() {
  const navigate = useNavigate();

  const { notification, notifyError, notifySuccess } = useNotifications();

  const { mutateAsync, isPending } = useUpdateProfile();
  const { mutateAsync: updatePhoneAsync, isPending: isPhonePending } =
    useUpdateProfilePhone();

  const { data: profileData, isLoading } =
    useGetMyUserDetailsFromAuthBESession();

  const formik = useFormik({
    initialValues: {
      firstName: capitalize(profileData?.firstName) ?? "",
      lastName: capitalize(profileData?.lastName) ?? "",
      email: profileData?.email ?? "",
      phone: profileData?.phone ?? "",
      street1: profileData?.street1 ?? "",
      street2: profileData?.street2 ?? "",
      city: profileData?.city ?? "",
      state: profileData?.state ?? "",
      countryCode: profileData?.countryCode ?? "",
      zipCode: profileData?.zipCode ?? "",
    },
    enableReinitialize: true,
    initialTouched: {
      street1: true,
    },
    validationSchema: Yup.object({
      firstName: Yup.string().required("Please enter first name"),
      lastName: Yup.string().required("Please enter last name"),
      phone: Yup.string().required("Please enter phone number"),
      street1: Yup.string().required("Please enter street"),
      city: Yup.string().required("Please enter city"),
      state: Yup.string().required("Please enter state"),
      zipCode: Yup.string().required("Please enter zip code"),
      countryCode: Yup.string().required("Please select country"),
    }),
    onSubmit: async (values) => {
      const errors = await formik.validateForm();

      if (Object.keys(errors).length > 0) {
        notifyError("Please correct fields.");
        return;
      }

      try {
        // Phone has its own endpoint; the profile route rejects a phone field.
        const { phone, email, ...profilePayload } = values;

        await mutateAsync(profilePayload);

        if (phone && phone !== profileData?.phone) {
          await updatePhoneAsync({ phone });
        }

        notifySuccess({ title: "Success", description: "Profile updated." });
      } catch (err: any) {
        const status = err?.response?.data?.statusCode;
        if (status === 409) {
          notifyError("That phone number is already in use.");
        } else if (status === 400) {
          notifyError("Please enter a valid phone number for your country.");
        } else {
          notifyError("Whoops! Please try again.");
        }
      }
    },
  });

  return (
    <div className="min-h-screen max-w-2xl mx-auto px-4 sm:px-6 py-4">
      <main className="">
        {notification}

        <form>
          {/* Back Button */}
          <Button type="link" onClick={() => navigate("/home")}>
            Back
          </Button>

          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-bold my-4">Profile</h1>

            {(isLoading || isPending || isPhonePending) && (
              <Spin size="default" />
            )}

            <Button
              className=""
              type="primary"
              onClick={() => formik.handleSubmit()}
              disabled={Object.keys(formik.errors).length > 0}
              loading={isPending || isPhonePending}
            >
              Save
            </Button>
          </div>

          <div className="space-y-6 bg-slate-50 mb-10">
            <div>
              <label
                htmlFor="firstName"
                className="text-sm font-semibold mb-2 block"
              >
                First Name <span className="text-red-500">*</span>
              </label>
              <Input
                placeholder="John Doe"
                className={`h-10`}
                {...formik.getFieldProps("firstName")}
              />
            </div>

            <div>
              <label
                htmlFor="lastName"
                className="text-sm font-semibold mb-2 block"
              >
                Last Name <span className="text-red-500">*</span>
              </label>
              <Input
                placeholder="John Doe"
                className={`h-10 ${!!formik.errors.lastName ? "border-red-500 border" : ""}`}
                {...formik.getFieldProps("lastName")}
              />
            </div>

            <div>
              <label
                htmlFor="fullName"
                className="text-sm font-semibold mb-2 block"
              >
                Email <span className="text-red-500">*</span>
              </label>
              <Input
                disabled
                className={`h-10`}
                placeholder="jdoe@gmail.com"
                {...formik.getFieldProps("email")}
              />
            </div>

            <div>
              <label
                htmlFor="phone"
                className="text-sm font-semibold mb-2 block"
              >
                Phone <span className="text-red-500">*</span>
              </label>
              <Input
                className={`h-10 ${!!formik.errors.phone ? "border-red-500 border" : ""}`}
                placeholder="eg. 1234567890"
                {...formik.getFieldProps("phone")}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Enter your number without the country code.
              </p>
            </div>

            <div>
              <label
                htmlFor="street1"
                className="text-sm font-semibold mb-2 block"
              >
                Street 1 <span className="text-red-500">*</span>
              </label>
              <Input
                placeholder="123 Main St."
                className={`h-10 ${!!formik.errors.street1 ? "border-red-500 border" : ""}`}
                {...formik.getFieldProps("street1")}
              />
            </div>

            <div>
              <label
                htmlFor="street2"
                className="text-sm font-semibold mb-2 block"
              >
                Street 2
              </label>
              <Input
                placeholder="Apt 101"
                className={`h-10`}
                {...formik.getFieldProps("street2")}
              />{" "}
            </div>

            <div>
              <label
                htmlFor="city"
                className="text-sm font-semibold mb-2 block"
              >
                City <span className="text-red-500">*</span>
              </label>
              <Input
                placeholder="eg. New York City"
                className={`h-10 ${!!formik.errors.city ? "border-red-500 border" : ""}`}
                {...formik.getFieldProps("city")}
              />{" "}
            </div>

            <div>
              <label
                htmlFor="state"
                className="text-sm font-semibold mb-2 block"
              >
                State <span className="text-red-500">*</span>
              </label>
              <Input
                placeholder="eg. New York"
                className={`h-10 ${!!formik.errors.state ? "border-red-500 border" : ""}`}
                {...formik.getFieldProps("state")}
              />{" "}
            </div>

            <div>
              <label
                htmlFor="country"
                className="text-sm font-semibold mb-2 block"
              >
                Country<span className="text-red-500">*</span>
              </label>
              <select
                className="border px-10 py-2 rounded-lg"
                value={formik.values.countryCode}
                onChange={(e) =>
                  formik.setFieldValue("countryCode", e.target.value)
                }
              >
                {[
                  { label: "Australia", value: COUNTRY_CODE.AUSTRALIA },
                  { label: "Brazil", value: COUNTRY_CODE.BRAZIL },
                  { label: "Canada", value: COUNTRY_CODE.CANADA },
                  { label: "France", value: COUNTRY_CODE.FRANCE },
                  { label: "India", value: COUNTRY_CODE.INDIA },
                  { label: "Indonesia", value: COUNTRY_CODE.INDONESIA },
                  { label: "Mexico", value: COUNTRY_CODE.MEXICO },
                  { label: "Singapore", value: COUNTRY_CODE.SINGAPORE },
                  { label: "United Kingdom", value: COUNTRY_CODE.UK },
                  {
                    label: "United States Of America",
                    value: COUNTRY_CODE.USA,
                  },
                ].map((item, i) => (
                  <option key={i} value={item.value}>
                    {capitalizeFirst(item.label)}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label
                htmlFor="zipCode"
                className="text-sm font-semibold mb-2 block"
              >
                Zip Code <span className="text-red-500">*</span>
              </label>
              <Input
                placeholder="eg. 12345"
                className={`h-10 ${!!formik.errors.state ? "border-red-500 border" : ""}`}
                {...formik.getFieldProps("zipCode")}
              />{" "}
            </div>
          </div>
        </form>

        <DeleteAccount />
      </main>
    </div>
  );
}

function DeleteAccount() {
  const navigate = useNavigate();
  const { notification, notifyError } = useNotifications();

  const { mutateAsync: deleteUserAsync, isPending: isDeleteLoading } =
    useDeleteMe();

  const [deleteConf, showDeleteConf] = useState(false);

  const handleDeletion = () => {
    deleteUserAsync(undefined)
      .then(() => {
        navigate("/login?terminated=true");
      })
      .catch(() => {
        notifyError("Whoops! Something went wrong. Please try again.");
      });
  };

  return (
    <div className="">
      {notification}
      <ConfirmDialog
        open={deleteConf}
        title={"Are you sure you want to delete your account?"}
        subtitle="This will permanently delete all your data from all ExpenseHut products. Do you wish to continue?"
        onCancel={() => showDeleteConf(false)}
        onOk={handleDeletion}
      />

      <hr className="my-10" />

      <div className="my-20 pb-10">
        <div
          style={{
            border: "1px solid #ffb2b2",
            background: "#ffb2b2",
            borderRadius: 10,
            color: "",
          }}
          className="p-4"
        >
          <strong>Danger Zone</strong>
          <p className="my-4 text-sm text-gray-700">
            Deleting your account is irreversible. All your data will be
            permanently removed from our servers. Please proceed with caution.
          </p>
          <div className="my-6">
            <Button
              id="delete-account"
              type="primary"
              danger
              loading={isDeleteLoading}
              onClick={() => showDeleteConf(true)}
            >
              {" "}
              Delete Account
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
